// ── Moteur de quiz du TUTEUR ────────────────────────────────────────────────
// COPIE FIDÈLE de `src/QuizGame.jsx`. Tout est conservé : les cinq types
// d'exercice, le chrono qui rythme sans juger, le panneau de révélation, la
// prononciation, la jauge de force, les leurres du même champ lexical.
//
// Seuls les points qui ENTRENT EN COLLISION avec une séance prescrite sont
// changés, chacun signalé par un commentaire « TUTEUR » :
//   1. la liste est IMPOSÉE et FIGÉE — on ne recharge jamais de mots neufs en
//      cours de séance (c'est ce qui faisait qu'une prescription de dix mots
//      n'en révisait qu'un) ;
//   2. les mots passent EN REVUE, pas au hasard — dix prescrits, dix vus ;
//   3. aucune persistance de séance — c'est elle qui polluait la reprise du quiz
//      filtré avec des mots hors bande ;
//   4. la progression part vers `/api/tuteur/quiz/answer`.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  generateOptions,
  distractorPool,
  shareSense,
  getButtonClass,
  getGridClass,
  clozeDistractors,
  ADVANCE_MS_EXAMPLE,
  ADVANCE_MS_PLAIN,
} from './quizUtils';
import { useLang } from './lang';
import { findHighlightRanges } from './highlight';
import { WordBuild, WordReverse, WordCloze, WordListen } from './BonusGame';
import { jsonAuthHeaders } from '../apiAuth';

// Surligne le mot-vedette et toutes ses formes fléchies dans la phrase
// (matcher morphologique dans highlight.js). Préserve la casse/ponctuation
// d'origine en découpant la phrase sur les plages renvoyées.
const highlightWord = (sentence, word) => {
  if (!sentence || !word) return sentence;
  const ranges = findHighlightRanges(sentence, word);
  if (!ranges.length) return sentence;

  const parts = [];
  let last = 0;
  ranges.forEach(([start, end], i) => {
    if (start > last) parts.push(sentence.slice(last, start));
    parts.push(
      <strong key={i} className="text-white font-bold">{sentence.slice(start, end)}</strong>
    );
    last = end;
  });
  if (last < sentence.length) parts.push(sentence.slice(last));
  return <>{parts}</>;
};

// Chrono de la question. Il RYTHME SANS JUGER : à zéro on révèle la réponse et on
// passe, sans rien écrire dans le SRS ni dans la dose du jour. C'est le compromis
// retenu face à l'ancien chrono (celui en prod), qui comptait le temps écoulé comme
// une faute : `_compute_srs` remet alors reps à 0 et l'intervalle à 1 jour, donc un
// mot su depuis trois semaines repartait de zéro parce qu'on avait regardé ailleurs.
// La tension reste, la mémoire ne ment pas.
// Conséquences directes : pas de chiffre affiché (un décompte chiffré juge, une barre
// qui descend rythme) et pas de bouton pause (la pause n'a de sens que si le temps
// compte). L'ancienne carte avait les deux.
// Le chrono vaut pour TOUS les types d'exercice, avec un budget proportionné à ce
// qu'on demande : reconnaître une traduction est immédiat, lire une phrase à trou
// demande de la comprendre, reconstituer un mot lettre par lettre est une tâche
// manuelle. Sans ça, seul le QCM était rythmé et les exercices de production
// laissaient la carte figée indéfiniment, barre du haut morte.
const QUESTION_MS = { mc: 15000, listen: 18000, reverse: 15000, cloze: 22000, build: 35000 };

// Durée pendant laquelle les boutons restent colorés (vert la bonne réponse, rouge
// celle qu'on a touchée) avant que la grille ne cède la place au panneau de
// révélation. Assez pour lire le verdict d'un coup d'œil, assez court pour que la
// carte serve ensuite à apprendre plutôt qu'à juger.
const FLASH_MS = 800;

// LA barre de la carte, en deux phases : elle se vide pendant la question (chrono),
// puis se vide à nouveau pendant le feedback avant la carte suivante. Un seul objet
// pour deux rôles, au lieu d'un chrono en haut et d'une barre d'avance en bas.
// Elle est posée À FLEUR DU BORD SUPÉRIEUR de la carte, sans marge : elle se lit
// comme l'arête de la carte qui se consume, pas comme un widget de plus. Elle n'a
// plus de libellé « suivant → » (c'est toute la carte qui devient cliquable pendant
// le feedback) : un objet de moins, et la hauteur ne bouge plus entre les phases.
const PaceLine = ({ durationMs, tone = 'blue' }) => {
  const [w, setW] = useState(100);
  useEffect(() => {
    if (!durationMs) return;
    const id = setTimeout(() => setW(0), 20);
    return () => clearTimeout(id);
  }, [durationMs]);

  return (
    <div className="h-[3px] w-full bg-slate-700/40 overflow-hidden" aria-hidden="true">
      {durationMs ? (
        <div
          className={tone === 'wrong' ? 'h-full bg-amber-400/90' : 'h-full bg-blue-400/90'}
          style={{ width: `${w}%`, transition: `width ${durationMs}ms linear` }}
        />
      ) : null}
    </div>
  );
};

// Réglage du son : c'était un DISQUE BLEU PLEIN, donc l'aplat de couleur le plus
// fort de l'écran — il criait plus que le mot russe, alors que c'est un réglage
// secondaire. La DA réserve le bleu plein à l'action principale. Ici : icône seule,
// gris, qui s'éclaircit au survol.
const SoundButton = ({ enabled, onToggle, lang }) => (
  <button
    onClick={onToggle}
    className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-300 transition-colors"
    title={enabled ? lang.ui.soundOn : lang.ui.soundOff}
  >
    {enabled ? (
      <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-current">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-current opacity-60">
        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
      </svg>
    )}
  </button>
);

// Progression dans la série (10 par défaut) : une rangée de pastilles dans l'entête
// de la carte. Se remplit à chaque réponse, se vide au début d'une nouvelle série —
// donne un début et une fin explicites sans texte ni chrono. Bleu (neutre), jamais
// vert (réservé au « juste »).
// Dix points de 2 px se lisaient comme de la poussière sur la carte. Des segments
// (des tirets) portent la même information — où j'en suis, combien il reste — mais
// se lisent d'un coup d'œil comme une progression.
const SeriesDots = ({ done, total }) => (
  <span className="flex items-center gap-[3px]" title={`${done} / ${total}`} aria-label={`${done} / ${total}`}>
    {Array.from({ length: total }, (_, i) => (
      <span key={i} className={`h-[3px] w-3 rounded-full transition-colors ${i < done ? 'bg-blue-400' : 'bg-slate-700'}`} />
    ))}
  </span>
);

const PlayButton = ({ onClick, title }) => (
  <button
    onClick={(e) => { window.umami?.track?.('audio_play'); onClick?.(e); }}
    className="shrink-0 opacity-30 hover:opacity-80 transition-opacity"
    title={title}
  >
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-blue-300">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
    </svg>
  </button>
);

// Jauge de force du mot (0 → 5), dérivée des répétitions SRS. Affichée UNIQUEMENT
// quand le mot était échu et vient d'être rattrapé à temps (pulse ambré) : c'est un
// événement, ça mérite un signal. Affichée après chaque bonne réponse, elle redisait
// à la carte près ce que le rail mémoire dit déjà au bilan de série, et devenait du
// mobilier qu'on ne lit plus.
const ForceGauge = ({ reps, pulse }) => {
  const filled = Math.min(Math.max(reps ?? 0, 0), 5);
  return (
    <span className={`inline-flex items-center gap-0.5 ${pulse ? 'animate-pulse' : ''}`} aria-label={`force ${filled}/5`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={`w-3.5 h-1.5 rounded-full ${
            i < filled ? (pulse ? 'bg-amber-400' : 'bg-blue-400') : 'bg-slate-600'
          }`}
        />
      ))}
    </span>
  );
};

// Nombre de jours (≥ 1) jusqu'à la prochaine révision, depuis une date ISO (jour).
// Échelle de production (chantier C) : type d'exercice selon la force SRS du mot.
//   0-1 → QCM RU→FR (reconnaissance) ; 2-3 → QCM FR→RU (WordReverse) ;
//   4+  → reconstruction par tuiles (WordBuild). Repli sur un mode plus simple si
//   le mot ne s'y prête pas (trop court/long pour les tuiles, pas assez de leurres).
const canBuildWord = (w) => {
  const s = w?.russe || '';
  const n = [...s].length;
  return !/\s/.test(s) && n >= 3 && n <= 10;
};
const enoughDistinct = (word, pool) => {
  const distinct = new Set((pool || []).filter((w) => w.id !== word.id && w.russe).map((w) => w.russe));
  return distinct.size >= 3;
};
// Choix de l'exercice. Deux étages :
//
//   force 0-1 → QCM RU→FR. Un mot fragile se reconnaît avant de se produire.
//   force 2+  → ROTATION entre toutes les modalités jouables pour ce mot.
//
// La rotation est la correction d'un vrai défaut signalé : `build` était renvoyé
// dès que force ≥ 3, donc un mot bien su revenait indéfiniment sous forme de
// tuiles, toujours le même geste. On tourne maintenant sur les modalités
// disponibles, indexées par la force elle-même — et comme la force monte à chaque
// réussite, le mot change d'exercice à chaque fois qu'on le revoit, exactement ce
// qui était demandé. Aucun stockage supplémentaire : la force fait déjà foi.
//
// L'ordre de la liste va du plus réceptif au plus productif ; un mot sans phrase
// d'exemple ou trop long pour les tuiles saute simplement les modalités
// correspondantes.
const playableModes = (word, pool, exampleField, wordField, soundOn = true) => {
  const modes = ['mc'];
  // `listen` EXIGE d'entendre le mot (le russe est masqué à l'écran) : on ne le
  // propose JAMAIS quand le son est coupé, sinon la question serait sans réponse
  // possible. Placé juste après `mc` : l'ordre de rotation est inchangé son activé.
  if (soundOn) modes.push('listen');
  // Texte à trou : seulement si l'on peut lui bâtir des leurres d'un autre champ
  // lexical. Sinon l'exercice accepterait plusieurs réponses (voir clozeDistractors)
  // et vaudrait mieux ne pas être posé du tout — la rotation a trois autres
  // modalités. Cas typique : une séance THÉMATIQUE, où tout le vivier partage le
  // même thème.
  if (word?.[exampleField] && clozeDistractors(word, pool, wordField, 3).length >= 3) modes.push('cloze');
  if (enoughDistinct(word, pool)) modes.push('reverse');
  if (canBuildWord(word)) modes.push('build');
  return modes;
};

export const exerciseModeFor = (word, pool, exampleField, wordField, soundOn = true) => {
  const force = word?.force ?? 0;
  if (force < 2) return 'mc';
  const modes = playableModes(word, pool, exampleField, wordField, soundOn);
  // On saute le QCM au-delà de la reconnaissance : il reste dans la liste comme
  // filet de sécurité si aucune autre modalité n'est jouable.
  const advanced = modes.filter((m) => m !== 'mc');
  const ring = advanced.length ? advanced : modes;
  return ring[(force - 2) % ring.length];
};

const QuizGame = ({
  words,
  activeFilter,
  lastIncorrectIds,
  setLastIncorrectIds,
  isLoading,
  error,
  userId,
  seedWordId,
  themes,
  chapterSlug,
  explore,
  filterTheme,
  landing,
  onStatsUpdate,
  sessionKey,
  soundEnabled,
  toggleSound,
  play,
  playAuto,
  onAnswered,
  onRescued,
  frozen,
  seriesDone,
  seriesTotal,
  whenAudioIdle,
  stopAudio,
}) => {
  const lang = useLang();

  // Reprise de session (retour même onglet depuis une fiche /mot/…) : lue une
  // seule fois au montage. La question en cours est réaffichée telle quelle,
  // timer remis à zéro, rien n'est re-posté côté SRS.
  // Un ATTERRISSAGE (mot /?mot= ou thème /?theme= via l'URL) prime sur la reprise,
  // exactement comme dans useRandomWords qui recompose alors la séance : sinon
  // le score et la liste « à revoir » de l'ancienne séance s'affichent sur la
  // nouvelle, et `quiz_start` n'est jamais émis pour cette arrivée. Un thème
  // simplement restauré n'est PAS un atterrissage : on reprend la séance (avec
  // son badge), sinon le quiz thématique se perdait au retour depuis une fiche.
  // TUTEUR (3) : aucune reprise de séance. Une série prescrite est transitoire.
  const restored = null;

  // Historique de séance (score + liste « à revoir » + compteurs de retry). On le
  // restaure AUSSI sur un atterrissage-mot (/?mot=), typiquement quand on clique un
  // mot de la liste « à revoir » puis qu'on revient au quiz : la 1re carte est alors
  // le mot cliqué (via la recette d'amorce), mais le score et les erreurs NE doivent
  // PAS disparaître. Arrivée externe (aucune session récente) → tout repart à zéro.
  const historyRef = useRef();
  if (historyRef.current === undefined) {
    historyRef.current = null;
  }
  const history = historyRef.current;

  // Mot d'atterrissage (/?mot=…) : forcé en 1re carte, consommé une seule fois.
  const seedWordIdRef = useRef(seedWordId ?? null);
  // Mots déjà posés dans cette série (revue séquentielle, cf. TUTEUR (2)).
  const revueRef = useRef(new Set());
  // Événement quiz_start : tiré une fois quand la 1re question apparaît. Une séance
  // restaurée OU continuée (historique présent) compte comme déjà démarrée.
  const startedRef = useRef(!!history);

  const [currentWord, setCurrentWord]               = useState(restored?.currentWord ?? null);
  const [options, setOptions]                       = useState(restored?.options ?? []);
  const [selectedOption, setSelectedOption]         = useState(null);
  // TUTEUR (3) : ces compteurs n'alimentaient que la sauvegarde de séance. Le
  // décompte est tenu par la page parente, qui sait combien de mots prescrits.
  const [, setCorrectAnswers] = useState(0);
  const [, setTotalAnswered]  = useState(0);
  const [feedback, setFeedback]                     = useState(null);
  const [isAnswered, setIsAnswered]                 = useState(false);
  const [incorrectWords, setIncorrectWords]         = useState(history?.incorrectWords ?? []);
  const [optionCount, setOptionCount]               = useState(restored?.optionCount ?? 4);
  const [noneCorrect, setNoneCorrect]               = useState(restored?.noneCorrect ?? false);
  const [flipped, setFlipped]                       = useState(false);
  const [correctRetryCounts, setCorrectRetryCounts] = useState(history?.correctRetryCounts ?? {});
  // Résultat SRS de la dernière réponse (renvoyé par /api/tuteur/quiz/answer) pour le bilan
  // post-réponse : force (répétitions), prochaine révision, « rattrapé à temps ».
  const [srsResult, setSrsResult] = useState(null);
  // Type d'exercice de la question courante : 'mc' | 'cloze' | 'reverse' | 'build'.
  const [exerciseMode, setExerciseMode] = useState('mc');
  // Score de la carte en cours ({ wordId, n }), renvoyé par /api/tuteur/quiz/answer. Porté
  // par un état plutôt qu'un simple nombre pour être rattaché à SA carte : le lot
  // suivant peut arriver avant la fin de l'animation.
  const [gained, setGained] = useState(null);
  // Durée du feedback d'un exercice de production, annoncée par le composant lui-même
  // (onSettled) : c'est lui qui décide quand la réponse est verrouillée. Sans ça la
  // barre du haut restait un rail mort sur les tuiles et le texte à trou, alors que
  // ces cartes ont exactement le même temps d'attente avant la suivante.
  const [exerciseFeedbackMs, setExerciseFeedbackMs] = useState(0);
  // Deuxième temps du feedback : la grille laisse place au panneau de révélation.
  const [showPanel, setShowPanel] = useState(false);
  // Chrono expiré sur un exercice de production : le composant révèle sa réponse.
  const [exerciseExpired, setExerciseExpired] = useState(false);
  const [prepareTick, setPrepareTick] = useState(0);   // cf. loadNext, TUTEUR (1)

  // Tuile « activer le son », montrée UNE fois par chargement de page au tout début,
  const feedbackRef = useRef(null);
  const questionRef = useRef(null);
  const panelRef = useRef(null);
  const consecutiveRef = useRef(history?.consecutive ?? 0);
  // Dernier mot posé : évite de reposer le MÊME mot deux fois de suite quand le
  // vivier est petit (ex. thème « nourriture » ∩ top 500 = 2 mots) — sinon la même
  // question tombe en boucle. Sans effet sur les gros viviers (tirage aléatoire).
  const lastWordIdRef = useRef(restored?.currentWord?.id ?? null);
  // Traduction de la carte précédente : sert à ne pas réafficher la même consigne
  // avec un autre mot russe (voir prepareQuestion).
  const lastAnswerRef = useRef(null);

  // Reset des stats de séance sur changement MANUEL de vivier (fréquence/thème),
  // jamais au montage : une séance restaurée doit garder ses stats. On compare la
  // valeur PRÉCÉDENTE de sessionKey (pas un flag « premier passage ») : c'est le
  // seul pattern sûr en StrictMode, qui invoque les effets deux fois au montage —
  // un flag basculé au 1er run laissait le 2e run vider la liste (bug « la liste
  // disparaît au retour, en dev/local »).
  const prevSessionKeyRef = useRef(sessionKey);
  useEffect(() => {
    if (prevSessionKeyRef.current === sessionKey) return; // montage ou re-invoke StrictMode
    prevSessionKeyRef.current = sessionKey;
    setCorrectAnswers(0);
    setTotalAnswered(0);
    setIncorrectWords([]);
    setCorrectRetryCounts({});
    consecutiveRef.current = 0;
  }, [sessionKey]); // eslint-disable-line react-hooks/exhaustive-deps


  const audioUrl = (id, type) => `/static/audio/${id}_${type}.mp3`;

  // Auto-play du mot russe à chaque nouvelle question (playAuto respecte le son).
  // Pas en mode production (reverse/build/cloze) : entendre le mot donnerait la
  // réponse. Dans CE cas on coupe explicitement ce qui jouait encore — sinon la
  // lecture lancée par la carte précédente se poursuit par-dessus la nouvelle, et
  // l'on entend un mot en en voyant un autre.
  // Garde-fou de relecture : l'effet se relance à chaque changement de ses
  // dépendances (et deux fois de plus en StrictMode), et `play()` redémarre le
  // fichier depuis zéro. On entendait donc la même prononciation se relancer deux
  // ou trois fois d'affilée, hachée. La ref retient ce qui a déjà été joué POUR
  // CETTE CARTE ; `prepareQuestion` la remet à zéro, donc un mot qui revient plus
  // tard est bien reprononcé.
  const autoPlayedRef = useRef(null);
  useEffect(() => {
    if (!currentWord || frozen) return;
    // L'exercice d'écoute lance SA propre lecture (et les effets enfants tournent
    // avant ceux du parent) : couper ici la ferait taire aussitôt.
    if (exerciseMode === 'listen') return;
    if (exerciseMode !== 'mc') { stopAudio?.(); return; }
    if (autoPlayedRef.current === currentWord.id) return;
    autoPlayedRef.current = currentWord.id;
    playAuto(audioUrl(currentWord.id, 'word'));
  }, [currentWord?.id, exerciseMode, frozen]); // eslint-disable-line react-hooks/exhaustive-deps

  // La phrase d'exemple se prononce à l'ouverture du panneau de révélation. C'est le
  // moment où l'attention est maximale (on vient d'apprendre si l'on avait bon) et
  // c'était jusqu'ici cinq secondes de silence : le son existait pour 100 % des mots,
  // à un clic que personne ne faisait.
  // Calage : panneau à 0,8 s, phrase de 1,6 s en médiane (2,7 s au pire), avance à
  // 5 s — la lecture finit largement avant, et `whenAudioIdle` couvre le cas long.
  // On entend donc le mot isolé à la question, puis le même mot dans une vraie
  // phrase à la révélation. `playAuto` respecte le réglage du son.
  useEffect(() => {
    if (!showPanel || frozen || exerciseMode !== 'mc') return;
    if (!currentWord?.[lang.exampleRuField]) return;
    playAuto(audioUrl(currentWord.id, lang.audioEx));
  }, [showPanel]); // eslint-disable-line react-hooks/exhaustive-deps

  // quiz_start (Umami) : une seule fois, à l'apparition de la 1re question.
  useEffect(() => {
    if (startedRef.current || !currentWord) return;
    startedRef.current = true;
    window.umami?.track?.('quiz_start', { seeded: seedWordId != null, lang: lang.apiLang });
  }, [currentWord]); // eslint-disable-line react-hooks/exhaustive-deps

  // L'audio du retournement est joué DANS le gestionnaire de tap (voir la vue
  // question), pour ne se déclencher que sur une action réelle de l'utilisateur :
  // vers l'exemple → audio de la phrase ; retour au mot → audio du mot.

  const clearTimers = useCallback(() => {
    clearTimeout(feedbackRef.current);
    clearTimeout(questionRef.current);
    clearTimeout(panelRef.current);
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  // Gel de la séance pendant l'écran « terminé » (dose du jour atteinte) : on annule
  // l'avance auto déjà programmée pour que le quiz ne tourne pas sous la modale
  // (plus d'audio, de chrono ni de mot suivant en arrière-plan).
  useEffect(() => {
    if (frozen) clearTimers();
  }, [frozen, clearTimers]);

  // Reprise après « continuer » : si la carte gelée était déjà répondue (QCM), on
  // enchaîne enfin sur la suivante — son avance auto avait été annulée par le gel.
  const prevFrozenRef = useRef(frozen);
  useEffect(() => {
    const was = prevFrozenRef.current;
    prevFrozenRef.current = frozen;
    if (was && !frozen && isAnswered) loadNext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frozen]);

  // TUTEUR (1) : la liste est IMPOSÉE et FIGÉE. `loadNext` ne va JAMAIS chercher
  // de mots neufs — le quiz public rappelle `fetchWords` après chaque réponse,
  // et c'est exactement ce qui faisait qu'une prescription de dix mots n'en
  // révisait qu'un. Ici on se contente de préparer la question suivante.
  const loadNext = useCallback(() => {
    setPrepareTick((t) => t + 1);
  }, []);

  // Réponse du QCM en attente de VALIDATION auprès du parent (dose du jour + bilan
  // de séance). Un ref, pas un state : on le lit depuis des timers.
  const pendingAnsweredRef = useRef(null);

  // Avance après une réponse au QCM. On ne prévient le parent (`onAnswered`) qu'ICI,
  // au moment d'enchaîner — PAS à l'instant de la réponse. Sinon, sur la dernière
  // carte de la série, le parent bascule aussitôt sur le bilan et masque le quiz :
  // le feedback de cette carte n'a jamais le temps de s'afficher (le bug corrigé).
  // En différant, le feedback est vu, PUIS la série se clôt.
  // Idempotent (garde `pendingAnsweredRef`) : le timer ET le tap peuvent l'appeler,
  // la carte n'est comptée qu'une fois.
  const advanceAfterAnswer = useCallback((currentIncorrect, currentRetryCounts) => {
    if (pendingAnsweredRef.current === null) return; // déjà avancé pour cette carte
    const correct = pendingAnsweredRef.current;
    pendingAnsweredRef.current = null;
    onAnswered?.(correct);
    // Cette réponse clôt-elle la série ? Alors le parent va geler le quiz et
    // relancera `loadNext` au « continuer » (effet prevFrozen) : on n'enchaîne pas
    // ici, pour ne pas charger un lot par-dessus le bilan. Garde-fou si les props
    // de série manquent : on enchaîne toujours (jamais de quiz bloqué).
    const endsSeries = seriesTotal ? (seriesDone + 1) >= seriesTotal : false;
    if (!endsSeries) loadNext(currentIncorrect, currentRetryCounts);
  }, [onAnswered, seriesDone, seriesTotal, loadNext]);

  // Avance depuis le panneau de feedback (tap OU auto-avance). Gère LES DEUX cas :
  // une réponse a été donnée (`pending` non nul → on compte la carte via
  // advanceAfterAnswer) OU le temps s'est écoulé (`pending` nul → on enchaîne
  // sans rien compter, comme le timeout). Indispensable : sans ça, taper après un
  // timeout appelait advanceAfterAnswer qui sortait aussitôt (pending nul) ET
  // clearTimers annulait l'avance auto → on restait bloqué sur la carte.
  const advanceFromPanel = useCallback(() => {
    if (pendingAnsweredRef.current !== null) { advanceAfterAnswer(); return; }
    // TUTEUR (7) : chrono écoulé. Dans le quiz public, une carte passée au chrono
    // n'est comptée nulle part et ce n'est pas grave — le vivier se renouvelle,
    // la séance n'a pas de fin définie. Sur une séance PRESCRITE, si, c'est grave :
    // la fin est « les dix mots ont été vus », donc deux chronos écoulés laissaient
    // le compteur à 8/10 alors que les dix cartes étaient passées, et le moteur
    // resservait des mots en boucle sans jamais pouvoir conclure.
    // On compte donc la carte comme VUE (`null` = passée, ni juste ni fausse) :
    // elle avance la série sans salir le score. Aucun risque de gonfler la dose
    // du jour : côté tuteur la dose se lit dans `user_progress`, que le chrono
    // n'écrit toujours pas.
    onAnswered?.(null);
    loadNext();
  }, [advanceAfterAnswer, loadNext, onAnswered]);

  // Enregistre une réponse dans le SRS (fire-and-forget) et alimente le bilan
  // post-réponse (force, prochaine révision, « rattrapé à temps »). Partagé par
  // le QCM (handleAnswer) et les exercices de production (handleExerciseDone).
  const postProgress = useCallback((word, correct, mode = 'mc') => {
    // TUTEUR (6) : le quiz public s'identifie par un `userId` passé en props et
    // refusait d'écrire sans lui. La route du tuteur, elle, s'identifie par le
    // JETON de session (owner_uid côté serveur) : il n'y a pas d'UUID à passer,
    // donc `userId` vaut null — et le garde-fou copié tel quel jetait
    // silencieusement CHAQUE réponse. Une série prescrite entière n'écrivait
    // rien dans le SRS : la dose du jour restait à zéro après l'avoir faite.
    if (!word) return;
    // TUTEUR (4) : route du tuteur, jamais la route publique.
    fetch('/api/tuteur/quiz/answer', {
      method: 'POST',
      headers: jsonAuthHeaders(),
      // `mode` : le back en a besoin pour le barème (produire vaut plus que
      // reconnaître) et ne peut pas le déduire. `strong` reste envoyé tel quel,
      // c'est lui qui règle l'avancée du SRS.
      body: JSON.stringify({ word_id: word.id, correct, strong: mode === 'build' }),
    })
      .then((r) => r.json())
      .then((d) => {
        // Score de la carte : affiché tel quel, en « +N » seul. 0 → rien du tout.
        if (d && d.gained > 0) setGained({ wordId: word.id, n: d.gained });
        if (d && d.streak !== undefined) onStatsUpdate?.(d);
        if (d && d.repetitions !== undefined) {
          setSrsResult({ wordId: word.id, repetitions: d.repetitions, nextReview: d.next_review, wasDue: !!d.was_due });
        }
        // Mot échu rattrapé à temps → « sauvé de l'oubli » (compteur de la dose du jour).
        if (correct && d && d.was_due) onRescued?.();
      })
      .catch(() => {});
  }, [onStatsUpdate, onRescued]);

  // Fin d'un exercice de production (WordReverse / WordBuild) : le composant a déjà
  // montré son feedback, on enregistre le résultat SRS, met à jour les compteurs
  // et enchaîne. Pas de timer ni de liste « à revoir » sur ces modes.
  const handleExerciseDone = useCallback((correct) => {
    clearTimers();
    // `null` = la carte rend la main sans verdict : soit le chrono a expiré, soit
    // l'exercice n'était pas constructible (texte à trou sans phrase exploitable).
    // TUTEUR (7) : dans les DEUX cas la carte doit quand même faire avancer la
    // série. Le quiz public pouvait l'ignorer — sa séance n'a pas de fin définie.
    // Ici, un seul mot rendu ainsi et la série prescrite ne pouvait plus atteindre
    // son compte : le moteur resservait des mots indéfiniment.
    if (correct === null) { onAnswered?.(null); loadNext(); return; }
    onAnswered?.(correct); // carte répondue → avance la dose du jour + stats de séance
    // `strong` = production réelle (tuiles) → le SRS avance normalement ; les modes
    // à choix (cloze, reverse) sont des preuves plus faibles → avance atténuée côté back.
    postProgress(currentWord, correct, exerciseMode);
    setTotalAnswered((p) => p + 1);
    if (correct) {
      setCorrectAnswers((p) => p + 1);
      consecutiveRef.current += 1;
    } else {
      consecutiveRef.current = 0;
    }
    loadNext();
  }, [currentWord, postProgress, loadNext, clearTimers, exerciseMode, onAnswered]);

  const prepareQuestion = useCallback(() => {
    if (!words?.length) return;

    // TOUJOURS 4 options. C'était un tirage [4,4,4,6,6,8] : la carte changeait de
    // hauteur d'un mot à l'autre (grille 2×2, 2×3 puis 2×4), et à 8 options on
    // servait un mur de texte, illisible sur téléphone. Une grille 2×2 constante
    // rend la carte stable et la lecture immédiate. La difficulté doit venir du
    // type d'exercice (l'échelle reconnaissance → contexte → production), pas du
    // nombre de leurres.
    const count = 4;
    // 1re carte d'un quiz amorcé : on démarre sur le mot d'atterrissage s'il est
    // dans le batch ; ensuite (ref consommée) tirage aléatoire habituel.
    let word;
    if (seedWordIdRef.current != null) {
      word = words.find((w) => w.id === seedWordIdRef.current);
      seedWordIdRef.current = null;
    }
    // TUTEUR (2) : on passe le lot EN REVUE, un mot après l'autre. Le tirage au
    // hasard AVEC REMISE du quiz public ne couvre que 6,9 mots distincts sur dix
    // questions — acceptable sur un vivier qui se renouvelle, absurde sur une
    // liste prescrite où chaque mot doit être vu une fois.
    if (!word) {
      const frais = words.filter((w) => !revueRef.current.has(w.id));
      if (frais.length) {
        word = frais[0];
        revueRef.current.add(word.id);
      } else {
        revueRef.current.clear();
      }
    }
    if (!word) {
      // On écarte le mot précédent tant qu'il reste une autre option (vivier > 1) :
      // pas deux fois la même carte d'affilée sur un petit thème.
      //
      // On écarte AUSSI tout mot dont la traduction partage un sens avec celle de la
      // carte précédente. Sans ça on servait « Cette, ce, celui » cinq fois de suite :
      // c'étaient cinq mots russes DIFFÉRENTS (Этот, Эта, Тот…), donc le garde-fou par
      // identifiant ne voyait rien — mais l'utilisateur, lui, relit exactement la même
      // consigne. Et sur les exercices de production (tuiles, sens inverse), la
      // traduction EST l'énoncé : la carte paraît littéralement identique.
      const others = words.filter(
        (w) => w.id !== lastWordIdRef.current && !shareSense(w[lang.wordField], lastAnswerRef.current)
      );
      const pickFrom = others.length
        ? others
        : words.filter((w) => w.id !== lastWordIdRef.current);
      word = (pickFrom.length ? pickFrom : words)[Math.floor(Math.random() * (pickFrom.length || words.length))];
    }
    if (!word) return;
    lastWordIdRef.current = word.id;
    lastAnswerRef.current = word[lang.wordField];

    // « Aucune de ces réponses » n'a de sens qu'avec assez de distracteurs
    // distincts pour que la question reste une question.
    const poolSize = distractorPool(words, word, lang.wordField).length;
    const showNoneOption = poolSize >= 3 && Math.random() < 0.15;
    const noneIsCorrect  = showNoneOption && Math.random() < 0.5;

    const opts = generateOptions(words, word, count, showNoneOption, noneIsCorrect, lang.wordField, lang.noneLabel);

    // Le pool dédoublonné peut être plus court que `count` : la grille suit le
    // nombre d'options réellement affichées.
    setOptionCount(opts.length);
    setNoneCorrect(noneIsCorrect);
    setCurrentWord(word);
    setOptions(opts);
    setExerciseMode(exerciseModeFor(word, words, lang.exampleRuField, lang.wordField, soundEnabled));
    setIsAnswered(false);
    setSelectedOption(null);
    setFeedback(null);
    setFlipped(false);
    setSrsResult(null);
    setGained(null);
    setExerciseFeedbackMs(0);
    setShowPanel(false);
    setExerciseExpired(false);
    autoPlayedRef.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words, prepareTick, lang.wordField, lang.noneLabel, lang.exampleRuField, soundEnabled]);

  // Au montage d'une séance restaurée, la question en cours est déjà en place : ne
  // pas la remplacer par un tirage aléatoire. On mémorise le lot `words` pour lequel
  // une question est déjà prête (StrictMode-safe : on compare l'identité de `words`,
  // pas un flag « premier passage » que le double-invoke ferait sauter → la question
  // restaurée était écrasée).
  //
  // TUTEUR (5) — LE PIÈGE DE LA COPIE. Dans le quiz public, `loadNext` va
  // CHERCHER un nouveau lot : `words` change à chaque carte, donc ce garde-fou
  // « déjà préparé pour ce lot » laisse passer la question suivante.
  // Ici, TUTEUR (1) fige la liste : `words` ne change JAMAIS de la séance. Le
  // garde-fou bloquait donc TOUTES les questions après la première — le quiz
  // restait figé sur la carte 1, et le tap de reprise (advanceFromPanel →
  // loadNext) retombait sur le même blocage : plus aucun moyen de repartir.
  // C'est pour ça qu'il y a `prepareTick` : il faut le mettre DANS la clé, sinon
  // il n'a aucun effet. Comparer un couple (lot, tick) reste sûr en StrictMode —
  // le double-invoke rejoue le même couple et ressort tout de suite.
  const preparedForRef = useRef(restored ? words : undefined);
  const preparedTickRef = useRef(restored ? 0 : -1);
  useEffect(() => {
    // Bilan de série affiché : on ne monte PAS la carte suivante. Les exercices de
    // production rendent la main en appelant `loadNext()` tout de suite (pas par un
    // timer, que le gel annulerait) : le lot suivant arrivait donc pendant le bilan
    // et la question se préparait dessous. Le quiz est seulement masqué, pas démonté,
    // donc `WordListen` se montait pour de bon — et il joue le mot à l'ouverture.
    // On entendait le premier mot de la série suivante par-dessus les résultats.
    // Le garde-fou de `frozen` sur l'auto-play du parent ne couvrait pas ce cas :
    // en mode écoute il passe la main à l'enfant, qui, lui, ignore le gel.
    // `frozen` est en dépendance : au « continuer », la carte se prépare enfin.
    if (frozen) return;
    if (preparedForRef.current === words && preparedTickRef.current === prepareTick) return;
    // `clearTimers()` est APRÈS le garde-fou, pas avant. Avant, il s'exécutait à
    // chaque recréation de `prepareQuestion` — dont celles qui ne préparent rien.
    // Basculer le son en pleine carte suffisait : la fonction changeait d'identité,
    // l'effet passait, les timers mouraient, et le garde-fou ressortait sans rien
    // reconstruire. Carte sans chrono et sans avance auto, immobile.
    clearTimers();
    preparedForRef.current = words;
    preparedTickRef.current = prepareTick;
    prepareQuestion();
  }, [words, prepareTick, prepareQuestion, clearTimers, frozen]);

  // Temps écoulé sur la question. On révèle la réponse et on enchaîne, et c'est
  // TOUT : aucun appel à `postProgress` (le SRS ne doit pas enregistrer un mot comme
  // oublié alors qu'on ne sait pas s'il l'était), aucun `onAnswered` (sinon on
  // terminerait sa dose du jour en regardant dans le vide), aucun compteur touché,
  // et le mot ne rejoint pas la liste « à revoir ». Le chrono donne le tempo, il ne
  // témoigne de rien.
  const handleTimeout = useCallback(() => {
    if (isAnswered) return;
    setIsAnswered(true);
    setSelectedOption(null);
    window.umami?.track?.('quiz_timeout');
    // Temps écoulé = l'utilisateur ne répond plus (parti ?). On révèle la réponse
    // mais on N'AVANCE PLUS TOUT SEUL : le défilement s'arrête ici. Un simple clic
    // sur la carte reprend et passe au suivant (advanceFromPanel). Pas de `advanceMs`
    // → la barre de rythme ne décompte pas (carte en pause, cf. paceDuration).
    setFeedback({ status: 'timeout' });
    setShowPanel(true);
  }, [isAnswered]);

  // Armement du chrono. Le handler passe par une ref pour que les dépendances de
  // l'effet se limitent aux vrais déclencheurs (nouvelle carte, réponse, gel) : sinon
  // la moindre recréation de `loadNext` relancerait le setTimeout alors que la barre,
  // elle, est remontée par la clé de la carte — chrono et barre désynchronisés.
  // Sur un exercice de production, l'expiration ne fait que lever un drapeau : c'est
  // le composant qui sait comment révéler SA réponse, et qui rendra la main via
  // onDone(null) — donc sans rien écrire dans le SRS, comme le timeout du QCM.
  const timeoutHandlerRef = useRef(handleTimeout);
  timeoutHandlerRef.current = handleTimeout;
  useEffect(() => {
    if (!currentWord || isAnswered || frozen || exerciseExpired) return;
    const ms = QUESTION_MS[exerciseMode] ?? QUESTION_MS.mc;
    questionRef.current = setTimeout(() => {
      if (exerciseMode === 'mc') timeoutHandlerRef.current();
      else setExerciseExpired(true);
    }, ms);
    return () => clearTimeout(questionRef.current);
  }, [currentWord?.id, exerciseMode, isAnswered, frozen, exerciseExpired]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswer = useCallback(
    (selectedOpt) => {
      if (isAnswered) return;

      setIsAnswered(true);
      setSelectedOption(selectedOpt);
      setSrsResult(null);
      clearTimers();

      const correct = noneCorrect
        ? selectedOpt === lang.noneLabel
        : selectedOpt === currentWord?.[lang.wordField];

      window.umami?.track?.('quiz_answer', { correct });
      panelRef.current = setTimeout(() => setShowPanel(true), FLASH_MS);
      // On NE prévient PAS le parent maintenant : la carte est validée à l'avance
      // (advanceAfterAnswer), pour que le feedback de la dernière carte s'affiche
      // avant que le bilan de série ne masque le quiz.
      pendingAnsweredRef.current = correct;

      // Le niveau (bande de fréquence) ne change JAMAIS tout seul : il reste celui
      // que l'utilisateur a choisi dans le filtre. Pas de passage auto à 1000.
      setTotalAnswered((prev) => prev + 1);

      // Enregistrement SRS (partagé avec les exercices de production).
      postProgress(currentWord, correct);

      if (correct) {
        consecutiveRef.current += 1;
        // Laisser le temps de lire la phrase d'exemple quand elle sert de
        // renforcement en contexte ; sinon avance rapide.
        const advanceMs = currentWord[lang.exampleRuField] ? ADVANCE_MS_EXAMPLE : ADVANCE_MS_PLAIN;
        setFeedback({ status: 'correct', message: currentWord[lang.wordField], advanceMs });
        setCorrectAnswers((prev) => prev + 1);

        // Mot déjà raté dans la séance et retrouvé : on incrémente son compteur de
        // reprises. Le calcul se fait ICI, hors updater : un updater est réinvoqué
        // par StrictMode, et y armer le chrono laissait un timer fantôme que
        // `clearTimers` ne connaissait pas (il tombait sur la question suivante).
        let retryCrc = null;
        if (incorrectWords.some((w) => w.id === currentWord.id)) {
          retryCrc = { ...correctRetryCounts, [currentWord.id]: (correctRetryCounts[currentWord.id] ?? 0) + 1 };
          setCorrectRetryCounts(retryCrc);
        }
        // À échéance du chrono : si un audio (ex. la phrase d'exemple) joue encore,
        // on attend sa fin avant d'enchaîner (cap de sécurité côté hook).
        feedbackRef.current = setTimeout(() => {
          whenAudioIdle(() => advanceAfterAnswer(incorrectWords, retryCrc));
        }, advanceMs);
      } else {
        consecutiveRef.current = 0;
        const correctLabel = noneCorrect
          ? lang.ui.noneCorrectLabel(currentWord[lang.wordField])
          : currentWord[lang.wordField];

        const advanceMs = currentWord[lang.exampleRuField] ? ADVANCE_MS_EXAMPLE : ADVANCE_MS_PLAIN;
        setFeedback({ status: 'incorrect', message: correctLabel, advanceMs });

        setIncorrectWords((prev) => {
          if (prev.some((w) => w.id === currentWord.id)) return prev;
          return [...prev, {
            id: currentWord.id,
            russe: currentWord.russe,
            page: currentWord.page,
            [lang.wordField]: currentWord[lang.wordField],
            [lang.exampleRuField]: currentWord[lang.exampleRuField],
            [lang.exampleLangField]: currentWord[lang.exampleLangField],
          }];
        });

        feedbackRef.current = setTimeout(() => { whenAudioIdle(() => advanceAfterAnswer()); }, advanceMs);
      }
    },
    [isAnswered, noneCorrect, currentWord, clearTimers, advanceAfterAnswer, incorrectWords,
     correctRetryCounts, lang, postProgress, whenAudioIdle]
  );

  // TUTEUR (3) : rien n'est écrit dans le navigateur.

  const gridClass = getGridClass(optionCount);

  // Réglage des deux phases de la barre de rythme (voir PaceBar).
  const paceDuration = exerciseMode !== 'mc'
    ? (exerciseFeedbackMs || QUESTION_MS[exerciseMode] || 0) // production : chrono, puis feedback
    : isAnswered
      // Timeout : pas de `advanceMs` → 0 → la barre ne décompte pas (carte en pause,
      // on attend le clic). Réponse donnée : décompte jusqu'à l'avance auto.
      ? (feedback?.advanceMs ?? 0)                          // phase feedback
      : QUESTION_MS.mc;                                     // phase question (chrono)

  if (isLoading && !words?.length)
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm">{lang.ui.loading}</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="text-center py-16 text-red-400">
        <p className="mb-3">{lang.ui.errorPrefix} {error}</p>
        <button
          onClick={() => { window.location.href = '/?tuteur'; }}
          className="px-5 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm font-medium transition-colors"
        >
          {lang.ui.retry}
        </button>
      </div>
    );

  if (!words?.length)
    return <div className="text-center py-16 text-slate-400">{lang.ui.noWords}</div>;

  return (
    <div className="flex flex-col gap-4">

      {/* ── Carte quiz ── */}
      <div className="relative bg-slate-800 border border-slate-700/50 rounded-2xl shadow-xl overflow-hidden">

        {/* Score de la carte, à la façon d'un jeu : il jaillit, monte, disparaît en
            une seconde. Rien à lire, pas de texte, et surtout rien qui reste :
            l'animation finit à opacity 0. Position absolue → aucun décalage de la
            mise en page. `key` par carte pour que l'animation rejoue à chaque fois.
            Ancré sur la CARTE, donc valable aussi pour les tuiles et le texte à trou. */}
        {gained && gained.wordId === currentWord?.id && (
          <span
            key={`gain-${gained.wordId}-${gained.n}`}
            aria-hidden="true"
            className="pointer-events-none absolute right-5 md:right-7 top-12 z-10 text-3xl font-extrabold text-emerald-300 tabular-nums drop-shadow animate-score-pop motion-reduce:animate-none motion-reduce:opacity-0"
          >
            +{gained.n}
          </span>
        )}

        {/* Barre de rythme À FLEUR DU BORD SUPÉRIEUR : chrono pendant la question,
            puis avance auto pendant le feedback. La clé change à chaque phase pour
            remonter l'animation. */}
        <PaceLine
          key={`pace-${currentWord?.id}-${isAnswered || exerciseFeedbackMs ? 'fb' : 'q'}`}
          durationMs={paceDuration}
          tone={(isAnswered && feedback?.status !== 'correct') || exerciseExpired ? 'wrong' : 'blue'}
        />

        {/* En-tête : réglage du son à gauche, progression de la série à droite. Deux
            éléments gris, volontairement effacés — la vedette est le mot russe. */}
        <div className="flex items-center justify-between px-5 md:px-7 pt-3 pb-1">
          <SoundButton enabled={soundEnabled} onToggle={toggleSound} lang={lang} />
          {seriesTotal > 0 && (
            <SeriesDots done={Math.min(seriesDone ?? 0, seriesTotal)} total={seriesTotal} />
          )}
        </div>

        {currentWord && (
          <div
            className={`px-5 md:px-7 pb-6 pt-1 ${showPanel ? 'cursor-pointer' : ''}`}
            /* Pendant la révélation, TOUTE la carte avance à la suivante : la cible
               fait la taille de la carte, et ça remplace le lien « suivant → » de
               11 px qui traînait au bout de la barre. */
            onClick={showPanel ? () => { clearTimers(); advanceFromPanel(); } : undefined}
          >
            {/* Plancher de hauteur COMMUN à tous les types d'exercice : sans lui, une
                carte de texte à trou faisait 415 px et un QCM 623 px, donc la carte
                changeait de taille d'un mot à l'autre. */}
            <div
              key={exerciseMode !== 'mc' ? `ex-${currentWord.id}` : `q-${currentWord.id}`}
              className="min-h-[20rem] flex flex-col justify-center animate-fade-in-up motion-reduce:animate-none"
            >
            {exerciseMode === 'build' ? (
              <WordBuild word={currentWord} lang={lang} playAuto={playAuto} onDone={handleExerciseDone} onSettled={setExerciseFeedbackMs} expired={exerciseExpired} />
            ) : exerciseMode === 'cloze' ? (
              <WordCloze word={currentWord} pool={words} lang={lang} play={play} playAuto={playAuto} onDone={handleExerciseDone} onSettled={setExerciseFeedbackMs} expired={exerciseExpired} />
            ) : exerciseMode === 'reverse' ? (
              <WordReverse word={currentWord} pool={words} lang={lang} onDone={handleExerciseDone} onSettled={setExerciseFeedbackMs} expired={exerciseExpired} />
            ) : exerciseMode === 'listen' ? (
              <WordListen word={currentWord} pool={words} lang={lang} play={play} playAuto={playAuto} onDone={handleExerciseDone} onSettled={setExerciseFeedbackMs} expired={exerciseExpired} />
            ) : (
              /* ── Vue question ── */
              <div className="flex flex-col gap-5" data-quiz-card="">
                {/* ── ZONE A : le mot russe, la vedette ──
                    Le bouton d'écoute est collé au mot, avec un ESPACEUR DE MÊME
                    LARGEUR à gauche : le mot tombe donc exactement au centre optique
                    de la carte, sans que l'icône parte flotter seule contre le bord
                    droit (essayé, elle s'y lisait comme un élément orphelin). */}
                <div
                  className={`min-h-[6.5rem] flex items-center justify-center text-center ${currentWord[lang.exampleRuField] && !isAnswered ? 'cursor-pointer select-none' : ''}`}
                  onClick={(e) => {
                    if (!currentWord[lang.exampleRuField] || isAnswered) return;
                    e.stopPropagation();
                    setFlipped((f) => {
                      const next = !f;
                      playAuto(audioUrl(currentWord.id, next ? lang.audioEx : 'word'));
                      return next;
                    });
                  }}
                >
                  <span className="w-4 shrink-0" aria-hidden="true" />
                  {flipped && !isAnswered ? (
                    <p lang="ru" className="text-base sm:text-lg md:text-xl text-blue-200 leading-relaxed break-words">
                      {highlightWord(currentWord[lang.exampleRuField], currentWord.russe)}
                    </p>
                  ) : (
                    <p lang="ru" className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-wide leading-tight break-all text-blue-300">
                      {currentWord.russe}
                    </p>
                  )}
                  <PlayButton
                    title={lang.ui.listen}
                    onClick={(e) => {
                      e.stopPropagation();
                      play(audioUrl(currentWord.id, flipped && !isAnswered ? lang.audioEx : 'word'));
                    }}
                  />
                </div>

                {/* ── ZONE B : les réponses, puis la révélation ──
                    Hauteur fixe partagée. Avant la réponse : la grille 2×2. Après :
                    d'abord ~0,8 s les mêmes boutons colorés (on voit instantanément
                    juste/faux), puis la grille cède la place au panneau de révélation.
                    Effacer les leurres EN PLACE laissait un ou deux boutons perdus au
                    milieu de trous — très visible sur téléphone. Ici chaque état
                    REMPLIT sa zone : pas de trou, et rien ne bouge autour. */}
                <div className="min-h-[11.5rem] flex flex-col justify-center">
                  {showPanel ? (
                    <div className="flex flex-col gap-3">
                      <p className="text-center text-lg md:text-xl font-semibold text-green-400 break-words">
                        {noneCorrect ? (
                          <><span lang="ru">{currentWord.russe}</span> · {currentWord[lang.wordField]}</>
                        ) : (
                          currentWord[lang.wordField]
                        )}
                      </p>

                      {/* Ce qu'on a répondu à tort : l'information la plus utile après
                          une erreur, c'est de savoir avec quoi on a confondu. */}
                      {feedback?.status === 'incorrect' && selectedOption && (
                        <p className="text-center text-xs text-red-400/80 -mt-2 break-words line-through">
                          {selectedOption}
                        </p>
                      )}

                      {srsResult && srsResult.wordId === currentWord.id && srsResult.wasDue && (
                        <div className="flex items-center justify-center">
                          <ForceGauge reps={srsResult.repetitions} pulse />
                        </div>
                      )}

                      {/* L'encart d'exemple est une ZONE D'ÉCOUTE, pas une zone de
                          passage : le tapoter rejoue la phrase. Toute la carte fait
                          avancer pendant la révélation, et le haut-parleur ne fait que
                          16 px : viser à côté — ce qui arrive tout le temps — coupait
                          le feedback au lieu de rejouer le son. La cible fait
                          maintenant toute la largeur de l'encart. */}
                      {currentWord[lang.exampleRuField] && (
                        <div
                          onClick={(e) => { e.stopPropagation(); play(audioUrl(currentWord.id, lang.audioEx)); }}
                          title={lang.ui.listen}
                          className="group relative bg-slate-900/40 border border-slate-700/60 hover:border-slate-600 rounded-xl px-4 py-3 text-left cursor-pointer transition-colors"
                        >
                          <p lang="ru" className="text-blue-200 text-sm leading-relaxed break-words pr-7">
                            {highlightWord(currentWord[lang.exampleRuField], currentWord.russe)}
                          </p>
                          {currentWord[lang.exampleLangField] && (
                            <p className="text-slate-400 text-xs leading-relaxed mt-1 break-words">
                              {highlightWord(currentWord[lang.exampleLangField], currentWord[lang.wordField])}
                            </p>
                          )}
                          {/* Icône indicative : à 30 % d'opacité elle était invisible,
                              donc personne ne savait que la phrase se prononçait. */}
                          <svg viewBox="0 0 24 24" aria-hidden="true"
                               className="absolute right-2.5 top-3 w-4 h-4 fill-blue-300/70 group-hover:fill-blue-300 transition-colors">
                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                          </svg>
                        </div>
                      )}

                      <p className="text-center text-[11px] text-slate-600">{lang.ui.next}</p>
                    </div>
                  ) : (
                    <>
                      {/* L'invitation à retourner la carte, JUSTE SOUS LE MOT : c'est
                          le mot qu'on tape pour l'obtenir. Sous les boutons, elle
                          désignait une cible située à l'autre bout de la carte. */}
                      <p className="mb-3 h-4 text-center text-[11px] text-slate-600">
                        {!isAnswered && !flipped && currentWord[lang.exampleRuField] ? lang.ui.tapForExample : ''}
                        {!isAnswered && flipped ? lang.ui.tapToSeeWord : ''}
                      </p>
                      <div className={gridClass}>
                        {options.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleAnswer(option)}
                            disabled={isAnswered}
                            className={`px-4 py-4 rounded-xl text-sm md:text-base font-medium border transition-all break-words hyphens-auto ${
                              getButtonClass(option, isAnswered, noneCorrect, currentWord, selectedOption, lang.noneLabel, lang.wordField)
                            } ${isAnswered ? 'cursor-default' : 'cursor-pointer active:scale-95'}`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* L'avance auto et son raccourci « suivant → » vivent désormais dans la
                    barre du haut (PaceBar), qui sert déjà de chrono à la question. */}
              </div>
            )}
            </div>
          </div>
        )}
      </div>

      {/* ── Liste des erreurs ── */}
      {incorrectWords.length > 0 && (
        <div className="bg-slate-800/60 rounded-2xl px-5 md:px-7 py-4 border border-slate-700/40">
          <h3
            title={lang.ui.reviewTooltip}
            className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 cursor-default"
          >
            {lang.ui.toReview(incorrectWords.length)}
          </h3>
          <div className="flex flex-col gap-2">
            {incorrectWords.map((word) => {
              const retryCount = correctRetryCounts[word.id] ?? 0;
              const isDone = retryCount >= 2;
              return (
                <div
                  key={word.id}
                  className={`flex items-center justify-between text-sm ${isDone ? 'opacity-30' : ''}`}
                >
                  {word.page ? (
                    <a href={word.page} className="hover:underline decoration-slate-500">
                      <span lang="ru" className="text-blue-300 font-medium">{word.russe}</span>
                      <span className="text-slate-600 mx-2">·</span>
                      <span className="text-slate-300">{word[lang.wordField]}</span>
                    </a>
                  ) : (
                    <span>
                      <span lang="ru" className="text-blue-300 font-medium">{word.russe}</span>
                      <span className="text-slate-600 mx-2">·</span>
                      <span className="text-slate-300">{word[lang.wordField]}</span>
                    </span>
                  )}
                  <span className="shrink-0 ml-3">
                    {isDone ? (
                      <span className="text-green-500 text-xs font-bold">✓</span>
                    ) : retryCount > 0 ? (
                      <span className="text-yellow-500 text-xs">{'●'.repeat(retryCount)}</span>
                    ) : (
                      <span className="text-red-500/50 text-xs">●</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          {/* Légende toujours visible : les pastilles n'étaient expliquées que dans un
              title=, invisible au tactile. */}
          <p className="text-[11px] text-slate-600 mt-3">{lang.ui.reviewLegend}</p>
        </div>
      )}
    </div>
  );
};

export default QuizGame;
